import React, { useMemo } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Modal from '../../../components/modal/modal';
import CustomTable from '../../../components/table/table';
import { getProjectTypeDisplayName } from '../../projects/project-list/get-project-type-name';
import '../../../components/modal/modal.scss';
import './resource-template-summary-modal.scss';

/**
 * Modal displaying the summary of templates updated when a Resource cost is modified.
 *
 * @param {Object} props - Component properties.
 * @param {boolean} props.open - Controls the visibility of the modal.
 * @param {Function} props.onClose - Callback triggered when the modal is closed.
 * @param {Object} props.summary - Summary details containing updated templates and counts.
 * @param {Array<Object>} [props.summary.itemTemplates] - List of updated item templates.
 * @param {Array<Object>} [props.summary.skippedItemTemplates] - List of skipped/already up-to-date item templates.
 * @param {Array<Object>} [props.summary.taskTemplates] - List of updated task templates.
 * @param {Array<Object>} [props.summary.skippedTaskTemplates] - List of skipped/already up-to-date task templates.
 * @param {Array<Object>} [props.summary.projects] - List of updated projects.
 * @param {Array<Object>} [props.summary.skippedProjects] - List of skipped/already up-to-date projects.
 * @param {number} [props.summary.itemTemplatesCount] - Count of updated item templates.
 * @param {number} [props.summary.skippedItemTemplatesCount] - Count of skipped item templates.
 * @param {number} [props.summary.taskTemplatesCount] - Count of updated task templates.
 * @param {number} [props.summary.skippedTaskTemplatesCount] - Count of skipped task templates.
 * @param {number} [props.summary.projectsCount] - Count of updated projects.
 * @param {number} [props.summary.skippedProjectsCount] - Count of skipped projects.
 * @returns {JSX.Element} The rendered modal component.
 */
export default function ResourceTemplateSummaryModal({
  open,
  onClose,
  summary
}) {
  const itemTemplates = summary?.itemTemplates || [];
  const skippedItemTemplates = summary?.skippedItemTemplates || [];
  const taskTemplates = summary?.taskTemplates || [];
  const skippedTaskTemplates = summary?.skippedTaskTemplates || [];
  const projects = summary?.projects || [];
  const skippedProjects = summary?.skippedProjects || [];

  const itemTemplatesUpdatedCount = itemTemplates.filter((t) => (t.status || 'Updated') === 'Updated').length;
  const itemTemplatesUpToDateCount = skippedItemTemplates.filter((t) => (t.status || 'Already Up-to-date') === 'Already Up-to-date').length;
  const itemTemplatesNoResourceCount = skippedItemTemplates.filter((t) => t.status === 'No Matching Resource').length;

  const taskTemplatesUpdatedCount = taskTemplates.filter((t) => (t.status || 'Updated') === 'Updated').length;
  const taskTemplatesUpToDateCount = skippedTaskTemplates.filter((t) => (t.status || 'Already Up-to-date') === 'Already Up-to-date').length;
  const taskTemplatesNoResourceCount = skippedTaskTemplates.filter((t) => t.status === 'No Matching Resource').length;

  const projectsCount = summary?.projectsCount ?? projects.length;
  const projectsUpToDateCount = skippedProjects.filter((p) => (p.status || 'Already Up-to-date') === 'Already Up-to-date').length;
  const projectsNoResourceCount = skippedProjects.filter((p) => p.status === 'No Matching Resource').length;

  const revisedProjectsCount = projects.filter((p) => p.isNewRevision).length;
  const inPlaceProjectsCount = projects.filter((p) => p.isInPlace).length;

  const hasProjects = projects.length > 0 || skippedProjects.length > 0;

  const tableData = useMemo(() => {
    const items = itemTemplates.map((t) => ({
      id: t.id,
      name: t.name,
      tenderNumber: '-',
      type: 'Item Template',
      pillClass: 'pill-item-template',
      status: t.status || 'Updated'
    }));
    const skippedItems = skippedItemTemplates.map((t) => ({
      id: t.id,
      name: t.name,
      tenderNumber: '-',
      type: 'Item Template',
      pillClass: 'pill-item-template',
      status: t.status || 'Already Up-to-date'
    }));
    const tasks = taskTemplates.map((t) => ({
      id: t.id,
      name: t.name,
      tenderNumber: '-',
      type: 'Task Template',
      pillClass: 'pill-task-template',
      status: t.status || 'Updated'
    }));
    const skippedTasks = skippedTaskTemplates.map((t) => ({
      id: t.id,
      name: t.name,
      tenderNumber: '-',
      type: 'Task Template',
      pillClass: 'pill-task-template',
      status: t.status || 'Already Up-to-date'
    }));
    const resolveProjectType = (p) => {
      if (p.quote_type) {
        const displayName = getProjectTypeDisplayName(p.quote_type);
        if (displayName === 'Cost Plus') {
          return { typeLabel: 'Cost Plus', pillClass: 'pill-costplus' };
        }
        if (displayName === 'Fixed Price Quote') {
          return { typeLabel: 'Fixed Price Quote', pillClass: 'pill-quote' };
        }
        return { typeLabel: displayName, pillClass: 'pill-default' };
      }
      if (p.name && p.name.toLowerCase().includes('cost plus')) {
        return { typeLabel: 'Cost Plus', pillClass: 'pill-costplus' };
      }
      return { typeLabel: 'Fixed Price Quote', pillClass: 'pill-quote' };
    };

    const projs = projects.map((p) => {
      const { typeLabel, pillClass } = resolveProjectType(p);
      return {
        id: p.id,
        name: p.name,
        tenderNumber: p.tenderNumber || (p.revisionNumber ? `Rev.(${p.revisionNumber})` : '-'),
        hasRevision: Boolean(p.revisionNumber || p.isNewRevision),
        type: typeLabel,
        pillClass,
        status: p.status || 'Updated'
      };
    });
    const skippedProjs = skippedProjects.map((p) => {
      const { typeLabel, pillClass } = resolveProjectType(p);
      return {
        id: p.id,
        name: p.name,
        tenderNumber: p.tenderNumber || (p.revisionNumber ? `Rev.(${p.revisionNumber})` : '-'),
        hasRevision: Boolean(p.revisionNumber || p.isNewRevision),
        type: typeLabel,
        pillClass,
        status: p.status || 'Already Up-to-date'
      };
    });
    return [...items, ...skippedItems, ...tasks, ...skippedTasks, ...projs, ...skippedProjs];
  }, [itemTemplates, skippedItemTemplates, taskTemplates, skippedTaskTemplates, projects, skippedProjects]);

  const columns = useMemo(() => {
    const cols = [
      {
        field: 'name',
        title: 'Name',
        render: (row) => {
          const isUpdated = row.status === 'Updated';
          const isNoResource = row.status === 'No Matching Resource';
          return (
            <div className="d-flex align-items-center">
              {isUpdated ? (
                <CheckCircleOutlineIcon className="template-check-icon text-success me-2" fontSize="small" />
              ) : isNoResource ? (
                <WarningAmberIcon className="template-check-icon text-warning me-2" fontSize="small" />
              ) : (
                <InfoOutlinedIcon className="template-check-icon text-secondary me-2" fontSize="small" />
              )}
              <span>{row.name}</span>
            </div>
          );
        }
      }
    ];

    if (hasProjects) {
      const hasRevisions = tableData.some((r) => r.hasRevision);
      const hasPlainNumbers = tableData.some((r) => !r.hasRevision && r.tenderNumber && r.tenderNumber !== '-');

      let headerTitle = 'Project No.';
      if (revisedProjectsCount > 0 && inPlaceProjectsCount > 0) {
        headerTitle = 'Project / New Revision No.';
      } else if (revisedProjectsCount > 0) {
        headerTitle = 'New Revision No.';
      } else if (hasRevisions && hasPlainNumbers) {
        headerTitle = 'Project / Revision No.';
      } else if (hasRevisions) {
        headerTitle = 'Revision No.';
      }

      cols.push({
        field: 'tenderNumber',
        title: headerTitle,
        render: (row) => (
          <span className="fw-semibold text-secondary">
            {row.tenderNumber || '-'}
          </span>
        )
      });
    }

    cols.push({
      field: 'type',
      title: 'Type',
      render: (row) => (
        <span className={`summary-type-pill ${row.pillClass || 'pill-default'}`}>
          {row.type}
        </span>
      )
    });

    cols.push({
      field: 'status',
      title: 'Status',
      render: (row) => {
        let pillModifier = 'pill-status-uptodate';
        if (row.status === 'Updated') {
          pillModifier = 'pill-status-updated';
        } else if (row.status === 'No Matching Resource') {
          pillModifier = 'pill-status-no-resource';
        }
        return (
          <span className={`summary-status-pill ${pillModifier}`}>
            {row.status}
          </span>
        );
      }
    });

    return cols;
  }, [hasProjects, revisedProjectsCount, inPlaceProjectsCount, tableData]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      className="resource-template-summary-dialog"
      modalHeading="Update Summary"
      modalCloseTxt="DONE"
      modalCloseBtn={onClose}
      showActionBtn
      primary
      dataCy="resource-template-summary-modal"
    >
      <div className="resource-template-summary-content mb-3">
        <div className="summary-banner mb-3">
          <InfoOutlinedIcon className="summary-banner-icon me-2" />
          <div className="summary-banner-text">
            {summary?.resourceName ? (
              <span>
                <strong>{summary.resourceName}</strong> has been updated. The template and project update status is summarized below:
              </span>
            ) : (
              <span>
                The changes have been processed. The update status is summarized below:
              </span>
            )}
          </div>
        </div>

        <table className="table table-borderless summary-counts-table mb-3">
          <tbody>
            {itemTemplatesUpdatedCount > 0 && (
              <tr>
                <td className="fw-medium">Item templates updated:</td>
                <td className="fw-bold">{itemTemplatesUpdatedCount}</td>
              </tr>
            )}
            {itemTemplatesUpToDateCount > 0 && (
              <tr>
                <td className="fw-medium">Item templates already up-to-date:</td>
                <td className="fw-bold text-muted">{itemTemplatesUpToDateCount}</td>
              </tr>
            )}
            {itemTemplatesNoResourceCount > 0 && (
              <tr>
                <td className="fw-medium">Item templates with no matching resource:</td>
                <td className="fw-bold text-muted">{itemTemplatesNoResourceCount}</td>
              </tr>
            )}
            {taskTemplatesUpdatedCount > 0 && (
              <tr>
                <td className="fw-medium">Task templates updated:</td>
                <td className="fw-bold">{taskTemplatesUpdatedCount}</td>
              </tr>
            )}
            {taskTemplatesUpToDateCount > 0 && (
              <tr>
                <td className="fw-medium">Task templates already up-to-date:</td>
                <td>{taskTemplatesUpToDateCount}</td>
              </tr>
            )}
            {taskTemplatesNoResourceCount > 0 && (
              <tr>
                <td className="fw-medium">Task templates with no matching resource:</td>
                <td className="fw-bold text-muted">{taskTemplatesNoResourceCount}</td>
              </tr>
            )}
            {revisedProjectsCount > 0 && (
              <tr>
                <td className="fw-medium">Quote revisions created:</td>
                <td className="fw-bold">{revisedProjectsCount}</td>
              </tr>
            )}
            {inPlaceProjectsCount > 0 && (
              <tr>
                <td className="fw-medium">Active projects updated (in-place):</td>
                <td className="fw-bold">{inPlaceProjectsCount}</td>
              </tr>
            )}
            {projectsCount > 0 && revisedProjectsCount === 0 && inPlaceProjectsCount === 0 && (
              <tr>
                <td className="fw-medium">Projects updated:</td>
                <td className="fw-bold">{projectsCount}</td>
              </tr>
            )}
            {projectsUpToDateCount > 0 && (
              <tr>
                <td className="fw-medium">Projects already up-to-date:</td>
                <td>{projectsUpToDateCount}</td>
              </tr>
            )}
            {projectsNoResourceCount > 0 && (
              <tr>
                <td className="fw-medium">Projects with no matching resource:</td>
                <td className="fw-bold text-muted">{projectsNoResourceCount}</td>
              </tr>
            )}
          </tbody>
        </table>

        {tableData.length > 0 && (
          <div className="template-table-wrapper">
            <CustomTable
              columns={columns}
              data={tableData}
              fileName="Resource Updated Templates"
              exportButton={true}
              enableSearch={true}
              toolbar={true}
              itemsPerPage={10}
              emptyMessage="No updated templates found"
              dataCy="updated-templates-table"
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
